import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { api } from "../../services/api";
import Constants from "../../services/constants";
import { hideLoader, showLoader } from "./loaderSlice";
import { showToast } from "./toastSlice";

const initialState = {
  data: {},
  projectList: [],
  projectCount: null,
  loading: false,
  error: null,
};

// Create an AbortController instance for canceling requests
let abortController = null;

export const getAnalytics = createAsyncThunk(
  "analytics/getData",
  async (
    {
      id,
      timeFilter,
      statusFilter,
      methodFilter,
      browserFilter,
      countryFilter,
      navigate,
    },
    { dispatch, getState }
  ) => {
    try {
      // Cancel any pending request
      if (abortController) {
        abortController.abort();
      }

      // Create new AbortController for this request
      abortController = new AbortController();

      // Plan check removed - analytics available for all premium users

      dispatch(showLoader());
      let currentUTC = new Date().toISOString();
      const queryParams = new URLSearchParams({
        time_option: timeFilter,
        utc_time: currentUTC,
        ...(statusFilter !== "all" && { status: statusFilter }),
        ...(methodFilter !== "all" && { method: methodFilter }),
        ...(browserFilter !== "all" && { browser: browserFilter }),
        ...(countryFilter !== "all" && { country: countryFilter }),
      }).toString();

      const res = await api(
        "get",
        `${Constants.END_POINT.analytics}${id}?${queryParams}`,
        {},
        { signal: abortController.signal }
      );

      if (res?.success && res?.data) {
        return res.data;
      }
      if (res?.status != 200) {
        dispatch(
          showToast({
            severity: "error",
            summary: res?.data?.message || res?.message,
          })
        );
        navigate("/analytics");
      }

      throw new Error(res.message || "Failed to fetch analytics");
    } catch (error) {
      if (error.name === "AbortError") {
        // Ignore abort errors
        return;
      }
      dispatch(showToast({ severity: "error", summary: error.message }));
      throw error;
    } finally {
      dispatch(hideLoader());
      // Clear the abortController after request completes
      abortController = null;
    }
  }
);

export const getProjectsForAnalyticsAction = createAsyncThunk(
  "analytics/getProjects",
  async ({ id, page = 1, limit = 10, navigate }, { dispatch }) => {
    try {
      const res = await api(
        "get",
        `${Constants.END_POINT.project_list}?id=${id}&type=for_analytics&limit=${limit}&page=${page}`,
        {}
      );

      if (res?.success && res?.data) {
        return {
          data: res.data,
          count: res.count,
        };
      }
      throw new Error(res.message || "Failed to fetch projects");
    } catch (error) {
      dispatch(showToast({ severity: "error", summary: error.message }));
      throw error;
    } finally {
    }
  }
);

export const toggleAnalyticsAction = createAsyncThunk(
  "analytics/toggleAnalytics",
  async ({ org_id, id, data }, { dispatch }) => {
    try {
      dispatch(showLoader());
      const res = await api(
        "post",
        `${Constants.END_POINT.project_update}${id}`,
        data
      );

      if (res?.success && res?.data) {
        dispatch(getProjectsForAnalyticsAction({ id: org_id }));
        dispatch(
          showToast({
            severity: "success",
            summary: "Analytics settings updated successfully",
          })
        );
        return res.data;
      }
      throw new Error(res.message || "Failed to update analytics settings");
    } catch (error) {
      dispatch(showToast({ severity: "error", summary: error.message }));
      throw error;
    } finally {
      dispatch(hideLoader());
    }
  }
);

const analyticsSlice = createSlice({
  name: "analytics",
  initialState,
  reducers: {
    setAnalyticsData: (state, action) => {
      state.data = action.payload;
    },
    setAnalyticsProjectList: (state, action) => {
      state.projectList = action.payload;
    },
    setProjectCount: (state, action) => {
      state.projectCount = action.payload;
    },
    resetAnalytics: (state) => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get Analytics
      .addCase(getAnalytics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAnalytics.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
        state.error = null;
      })
      .addCase(getAnalytics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // Get Projects
      .addCase(getProjectsForAnalyticsAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getProjectsForAnalyticsAction.fulfilled, (state, action) => {
        state.loading = false;
        state.projectList = action.payload.data;
        state.projectCount = action.payload.count;
        state.error = null;
      })
      .addCase(getProjectsForAnalyticsAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // Toggle Analytics
      .addCase(toggleAnalyticsAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(toggleAnalyticsAction.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(toggleAnalyticsAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

export const {
  setAnalyticsData,
  setAnalyticsProjectList,
  setProjectCount,
  resetAnalytics,
} = analyticsSlice.actions;

export default analyticsSlice.reducer;
